import { BookletEntries } from "./booklet-entry.js";
import { type BookletWord, BookletSheet } from "./booklet-sheet.js";
import { slot } from "./dom.js";
import { type PartState, PartStates } from "./part-state.js";
import { SheetSentences } from "./sheet-sentences.js";
import {
  concurrency,
  counted,
  pageFetch,
  type SheetKind,
  sheetTitle,
  type SheetToolProperties,
  startSheetTool,
} from "./sheet-tool.js";
import { GlyphLibrary } from "./worksheet-drawing.js";
import { HanziFacts } from "./worksheet-facts.js";
import { WritingSheet } from "./worksheet-sheet.js";
import { defaultState } from "./worksheet-state.js";
import { characterLimit, writingItems } from "./worksheet-tool.js";
import { sheetCharacters } from "./worksheet-words.js";

/**
 * The revision booklet: a reader's words as a booklet to study from on paper,
 * as a kind of sheet `sheet-tool.ts` draws (#1068).
 *
 * The words come from the sources every worksheet shares: a published List or
 * HSK band, the reader's saved words (all of them, one tag's or one band's) or
 * one of their own Lists. Each word's `canonical.json` and the 例句 it names
 * are fetched as the booklet needs them, and nothing else is.
 *
 * **Pro holds it.** A reader whose session hint says Pro gets the controls.
 * Everyone else gets the offer. The page is static and the data public, so
 * this is the same pseudo-gate `src/site/download-offer.ts` describes: it
 * asks on the one path a person in a browser takes.
 */

/** What a booklet holds besides its words, and whether each is in by default. */
export const bookletParts = new PartStates<"sentences" | "writing" | "test">([
  ["sentences", true],
  ["writing", false],
  ["test", false],
]);

/** A booklet's settings. */
export type BookletState = PartState<"sentences" | "writing" | "test">;

/** The most of a source's 例句 the sentences section prints. */
export const sentenceLimit = 200;

/** What drawing a booklet needs from its page. */
export interface BookletKindProperties {
  readonly entries: BookletEntries;
  readonly sentences: SheetSentences;
}

/** Whether a sentence uses one of these words, by the site's cut of it. */
function uses(
  pieces: readonly { readonly text: string }[],
  words: ReadonlySet<string>,
): boolean {
  return pieces.some((piece) => words.has(piece.text));
}

/** The revision booklet, drawn from the words its source names. */
export function bookletKind(
  properties: BookletKindProperties,
): SheetKind<BookletState> {
  return {
    read: (hash) => {
      const state = bookletParts.read(hash);

      // A booklet has no box to type words into, and a List is what most
      // booklets are drawn from.
      return state.source === "typed" ? { ...state, source: "list" } : state;
    },
    fragment: (state) => bookletParts.fragment(state),
    nothingAsked: () => undefined,
    title: (state, list) =>
      sheetTitle(state, list, {
        typed: "Revision booklet",
        saved: "My saved words",
      }),

    draw: async ({ state, words, sentences, entries, labels }) => {
      const found = [...(await properties.entries.some(words))].sort(
        (one, other) =>
          one.sortKey < other.sortKey
            ? -1
            : one.sortKey > other.sortKey
              ? 1
              : 0,
      );
      if (found.length === 0) {
        return "Could not load these words. Try again in a moment.";
      }
      const examples = new Map(
        (
          await properties.sentences.some(
            found.flatMap((entry) =>
              entry.liju === undefined ? [] : [entry.liju],
            ),
          )
        ).map((sentence) => [sentence.uuid, sentence]),
      );
      const printed: readonly BookletWord[] = found.map((entry) => ({
        entry,
        example:
          entry.liju === undefined ? undefined : examples.get(entry.liju),
      }));
      const shown = new Set(examples.keys());
      const held = new Set(found.map((entry) => entry.word));
      const listed = state.sentences
        ? (
            await properties.sentences.some(
              sentences.filter((uuid) => !shown.has(uuid)),
            )
          )
            .filter((sentence) => uses(sentence.pieces, held))
            .slice(0, sentenceLimit)
        : [];

      const library = new GlyphLibrary({ document });
      const writing = state.writing
        ? new WritingSheet({ library }).pages(
            { ...defaultState, paper: state.paper },
            await writingItems(
              sheetCharacters(
                found.map((entry) => entry.word),
                characterLimit,
              ),
              entries,
              library,
              HanziFacts.none(),
            ),
            { ...labels, title: `Writing practice: ${labels.title}` },
          )
        : [];

      const pages = new BookletSheet({ paper: state.paper, labels }).pages({
        words: printed,
        sentences: listed,
        writing,
        test: state.test,
      });

      return {
        nodes: [library.element, ...pages],
        status: `${counted(found.length, "word", "words")}${
          listed.length === 0
            ? ""
            : ` and ${counted(listed.length, "sentence", "sentences")}`
        } on ${counted(pages.length, "page", "pages")}.`,
      };
    },
  };
}

/**
 * Show the controls to a reader holding Pro, and the offer to everybody else.
 * @returns Whether the controls were shown.
 */
export function bookletGate(container: HTMLElement): boolean {
  const pro = document.documentElement.dataset["cbMembership"] === "pro";
  const signedIn = document.documentElement.dataset["cbSession"] === "in";
  const controls = slot(container, "booklet");
  const offer = slot(container, "booklet-offer");
  const signIn = slot(container, "booklet-sign-in");
  const upgrade = slot(container, "booklet-upgrade");
  if (controls !== undefined) {
    controls.hidden = !pro;
  }
  if (offer !== undefined) {
    offer.hidden = pro;
  }
  if (signIn !== undefined) {
    signIn.hidden = signedIn;
  }
  if (upgrade !== undefined) {
    upgrade.hidden = !signedIn;
  }

  return pro;
}

/** Start the revision booklet on its container, for a reader holding Pro. */
export function startBookletTool(
  container: HTMLElement,
  properties: SheetToolProperties = pageFetch,
): void {
  if (!bookletGate(container)) {
    return;
  }
  startSheetTool(
    container,
    bookletKind({
      entries: new BookletEntries({ fetch: properties.fetch, concurrency }),
      sentences: new SheetSentences({ fetch: properties.fetch, concurrency }),
    }),
    properties,
  );
}
